/**
 * useTouchFeedback.ts
 * Custom hook for enhanced mobile touch interactions with haptic and visual feedback
 * Provides: haptic vibration, touch feedback states, and optimized mobile gestures
 */

import { useRef, useCallback } from 'react';

interface TouchFeedbackOptions {
  enableHaptics?: boolean;
  feedbackType?: 'light' | 'medium' | 'heavy';
  duration?: number;
}

export const useTouchFeedback = (options: TouchFeedbackOptions = {}) => {
  const {
    enableHaptics = true,
    feedbackType = 'light',
    duration = 100
  } = options;

  const touchTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  /**
   * Trigger haptic feedback on supported devices
   */
  const triggerHaptic = useCallback((type: 'light' | 'medium' | 'heavy' = feedbackType) => {
    if (!enableHaptics || !('vibrate' in navigator)) return;

    const vibrationPatterns: Record<string, number | number[]> = {
      light: 10,
      medium: 20,
      heavy: 40,
      tap: [5, 5],
      double: [10, 15, 10],
      success: [10, 20, 10],
      error: [30, 10, 30]
    };

    try {
      navigator.vibrate?.(vibrationPatterns[type] || vibrationPatterns.light);
    } catch (e) {
      // Silently fail if vibration API is not available
    }
  }, [enableHaptics, feedbackType]);

  /**
   * Enhanced click handler with haptic feedback
   */
  const handleTouchClick = useCallback((callback: () => void, hapticType: 'light' | 'medium' | 'heavy' = 'light') => {
    return (e: React.TouchEvent | React.MouseEvent) => {
      triggerHaptic(hapticType);
      
      // Prevent double-tap zoom on mobile
      if (e.type === 'touchend') {
        e.preventDefault();
      }

      callback();
    };
  }, [triggerHaptic]);

  /**
   * Debounced touch handler to prevent rapid-fire touches
   */
  const handleDebouncedTouch = useCallback((callback: () => void, debounceMs: number = 300) => {
    return (e: React.TouchEvent | React.MouseEvent) => {
      if (touchTimeoutRef.current) {
        clearTimeout(touchTimeoutRef.current);
      }

      triggerHaptic('light');

      touchTimeoutRef.current = setTimeout(() => {
        callback();
      }, debounceMs);
    };
  }, [triggerHaptic]);

  /**
   * Long press handler with haptic feedback
   */
  const useLongPress = (callback: () => void, ms: number = 500) => {
    const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

    const handleTouchStart = useCallback(() => {
      timeoutRef.current = setTimeout(() => {
        triggerHaptic('medium');
        callback();
      }, ms);
    }, [callback, ms, triggerHaptic]);

    const handleTouchEnd = useCallback(() => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    }, []);

    return {
      onTouchStart: handleTouchStart,
      onTouchEnd: handleTouchEnd,
      onMouseDown: handleTouchStart,
      onMouseUp: handleTouchEnd
    };
  };

  return {
    triggerHaptic,
    handleTouchClick,
    handleDebouncedTouch,
    useLongPress
  };
};

export default useTouchFeedback;
